import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ConfirmPopupComponent } from '@sp-shared/containers/confirm-popup/confirm-popup/confirm-popup.component';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AuthService, NotificationService, PermissionsService } from '@sp-core-services';
import { TranslateService } from '@ngx-translate/core';
import { Roles } from '@sp-core/agreement-keys/roles.enum';
import { IUserNavMenu } from '../../models/user-nav-menu.interface';
import { OrderInfoPopupComponent } from '@sp-shared/components/order-info-popup/order-info-popup.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ServerErrorModel } from '@sp-core/models/app-models/server-error.model';
import { ServerStatusCode } from '@sp-core/agreement-keys/server-status-code.enum';

@Component({
  selector: 'sp-user-nav-item',
  templateUrl: './user-nav-item.component.html',
  styleUrls: ['./user-nav-item.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UserNavItemComponent implements OnInit, OnDestroy {
  public imgSrc: string;
  public destroy$: Subject<void> = new Subject();
  public userRoles: typeof Roles = Roles;
  public open: boolean;
  public arrowIcon = 'assets/images/arrow-down.svg';

  @Input() public item: IUserNavMenu;
  @Input() public user: IUser;
  @Input() public role: IRole;

  constructor(
    private authService: AuthService,
    public dialog: MatDialog,
    private permissionsService: PermissionsService,
    private cd: ChangeDetectorRef,
    private notificationService: NotificationService,
    private translate: TranslateService,
    public readonly _sanitizer: DomSanitizer,
  ) {}

  public ngOnInit(): void {
    this.imgSrc = this.item.icon;
  }

  public ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public openDialogConfirmLogOut(): void {
    const text = 'ARE_YOU_SURE_YOU_WANT_TO_LOG_OUT';
    this.getConfirmDialog(text).subscribe((result) => {
      if (result) {
        this.logOut();
      }
    });
  }

  private getConfirmDialog(text: string): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      data: { text },
    });

    return dialogRef.afterClosed().pipe(takeUntil(this.destroy$));
  }

  public logOut(): void {
    this.authService.logout().pipe(takeUntil(this.destroy$)).subscribe();
  }

  public updateRole(role: IRole) {
    this.open = !this.open;
    if (this.role.id === role.id) {
      return;
    }
    const text = `Are you sure you want to change role to '${role.name}'?`;
    this.getConfirmDialog(text)
      .pipe(
        filter(Boolean),
        takeUntil(this.destroy$),
        switchMap(() => {
          return this.permissionsService.updateRole(role);
        }),
      )
      .subscribe({
        next: () => {
          this.authService.handlerNavigation(role);
          this.cd.detectChanges();
        },
        // A stalled or rejected switch used to leave the user on the current role with no
        // explanation at all: neither a TimeoutError nor a 409 reaches HttpErrorInterceptor, so
        // nothing is shown unless it is shown here. 409 carries a message written for the user,
        // which is worth more than our generic wording.
        error: (error: ServerErrorModel) => {
          const message =
            error?.status === ServerStatusCode.customError && error?.message
              ? error.message
              : this.translate.instant('AUTH.ROLE_SWITCH_FAILED');

          this.notificationService.showNotification(message, 'warning');
        },
      });
  }

  public openOrderPopup(): void {
    this.dialog.open(OrderInfoPopupComponent, {
      disableClose: true,
      panelClass: 'order-info',
    });
  }

  public getSvgData(): SafeHtml {
    return this._sanitizer.bypassSecurityTrustHtml(this.item?.svg);
  }
}
